/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  GENERATED FILE — DO NOT EDIT                                            ║
 * ║                                                                          ║
 * ║  Source : functions/betMath.js                                           ║
 * ║  Regen  : npm run math:sync                                              ║
 * ║  Verify : npm run math:verify   (runs in the predeploy gate)             ║
 * ║                                                                          ║
 * ║  Edit the source, then re-run the sync. Hand edits here are overwritten  ║
 * ║  and will fail the gate.                                                 ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */

/**
 * betMath.js — THE canonical bet math core.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * THIS IS THE ONE FILE. Every odds conversion, devig, EV, R-factor, and Kelly
 * calculation in the platform must ultimately resolve to a function in here.
 *
 * Rules:
 *   1. PURE. No I/O, no Firestore, no `require` of anything. Deterministic.
 *   2. Every magic number is a NAMED, DOCUMENTED constant in LIMITS below.
 *   3. Numeric behavior is frozen by `functions/__tests__/betMath.test.js`
 *      golden vectors. Changing a formula must change a test on purpose.
 *   4. The frontend mirror `website/utils/betMath.js` is GENERATED from this
 *      file by `npm run math:sync`. Never edit the mirror by hand.
 *
 * Why a generated mirror instead of a shared import: Firebase deploys only the
 * `functions/` directory, and the Expo web app's Metro root is `website/`.
 * Neither can reach a common parent at runtime, so the file is authored once
 * here and mechanically copied. `npm run math:verify` fails the predeploy gate
 * if the two ever drift.
 * ════════════════════════════════════════════════════════════════════════════
 */

// ═══════════════════════════════════════════════════════════════════════════
// LIMITS — every clamp, cap, floor, and threshold in the platform's math.
// Previously these were ~30 inline magic numbers spread across 12 files.
// ═══════════════════════════════════════════════════════════════════════════

const LIMITS = {
  /** Fair probability clamp. A devigged prob can never leave this band. */
  PROB_MIN: 0.001,
  PROB_MAX: 0.999,

  /**
   * Additive devig is only trustworthy in the mid-range. Subtracting a flat
   * overround from a near-zero implied prob yields nonsense (e.g. -87% EV on a
   * +2000 longshot). Outside this band, additive is dropped from the min-set.
   */
  ADDITIVE_RELIABLE_MIN: 0.05,
  ADDITIVE_RELIABLE_MAX: 0.95,

  /**
   * Two-way total-implied trust band. Below DFS_FLOOR is a DFS/flex-pick shape
   * (not a real two-way market); above MALFORMED_CEILING means mispaired lines
   * or a triple-booked market. Either way we fall back to assumed juice.
   */
  TOTAL_IMPLIED_DFS_FLOOR: 0.50,
  TOTAL_IMPLIED_MALFORMED_CEILING: 1.50,

  /** Legacy flat assumed juice when only one side of a market exists. */
  ASSUMED_VIG_DEFAULT: 1.10,

  /** Opposite-odds sentinel sanitize range (API sends -99900 for "no line"). */
  OPPOSITE_ODDS_MIN: -5000,
  OPPOSITE_ODDS_MAX: 5000,

  /** Power-devig binary search bounds and iteration count. */
  POWER_K_LO: 0.01,
  POWER_K_HI: 20,
  POWER_ITERATIONS: 64,

  /** Default trim fraction for robustMean when n >= 4. */
  ROBUST_MEAN_TRIM: 0.2,

  /**
   * SGP R-factor bounds. R = legsDecimal / avgBookSgpDecimal measures how much
   * correlation the market is pricing in. R < 1 means legs are positively
   * correlated (books price the parlay shorter than independence implies).
   *
   * R_MIN_LEGACY is the ORIGINAL floor and the root cause of the phantom-EV
   * bug: a true R of 0.076 floored to 0.50 inflates fair probability ~6.5x,
   * producing a fake +578% edge. v2 math removes the floor entirely.
   */
  R_FACTOR_MAX: 1.30,
  R_FACTOR_MIN_LEGACY: 0.50,

  /** SGP correlated fair-probability clamp (tighter than PROB_MAX). */
  SGP_PROB_MIN: 0.001,
  SGP_PROB_MAX: 0.99,

  /** Kelly: maximum fraction of bankroll any single bet may be sized at. */
  KELLY_MAX_FRACTION: 0.50,

  /**
   * Display/sort EV caps applied by annotateCleanEv after contamination checks.
   * Props can legitimately show higher method EV than game lines; game lines above
   * ~35% are almost always data artifacts (alt-line mismatch, DFS outliers).
   */
  PROP_EV_DISPLAY_CAP: 35,
  LINE_EV_DISPLAY_CAP: 50,
};

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 1 — Odds conversions
//
// These replaced 27 separate copy-pasted implementations. All four are exact
// algebraic inverses of each other; `roundTripCheck` proves it.
// ═══════════════════════════════════════════════════════════════════════════

/** American odds → implied probability (vig included). -110 → 0.5238 */
function americanToImplied(american) {
  if (!Number.isFinite(american) || american === 0) return null;
  return american > 0
    ? 100 / (american + 100)
    : Math.abs(american) / (Math.abs(american) + 100);
}

/**
 * Guard against a finite input producing a non-finite result. Denormal inputs
 * (e.g. 5e-324) overflow to Infinity in these ratios, and an Infinity that
 * reaches the EV formula yields an Infinity edge. Found by property testing.
 */
function finiteOrNull(v) {
  return Number.isFinite(v) ? v : null;
}

/** American odds → decimal odds. -110 → 1.9091, +150 → 2.50 */
function americanToDecimal(american) {
  if (!Number.isFinite(american) || american === 0) return null;
  return finiteOrNull(american > 0
    ? american / 100 + 1
    : 100 / Math.abs(american) + 1);
}

/** Decimal odds → implied probability. 2.50 → 0.40 */
function decimalToImplied(decimal) {
  if (!Number.isFinite(decimal) || decimal <= 1) return null;
  return 1 / decimal;
}

/** Implied probability → decimal odds. 0.40 → 2.50 */
function impliedToDecimal(prob) {
  if (!Number.isFinite(prob) || prob <= 0 || prob >= 1) return null;
  return 1 / prob;
}

/**
 * Implied probability → American odds. 0.40 → +150, 0.5238 → -110
 *
 * Note the strict `>` at the 0.5 boundary. Even money can be written either
 * -100 or +100 — they are the same price — but +100 is the industry convention
 * and is what Crazy Ninja Math returns, so exactly 50% maps to +100. Verified
 * against the CNM API in betMath.oracle.test.js.
 */
function impliedToAmerican(prob) {
  if (!Number.isFinite(prob) || prob <= 0 || prob >= 1) return null;
  return finiteOrNull(prob > 0.5
    ? -(prob / (1 - prob)) * 100
    : ((1 - prob) / prob) * 100);
}

/** Decimal odds → American odds. 1.9091 → -110 */
function decimalToAmerican(decimal) {
  const p = decimalToImplied(decimal);
  return p === null ? null : impliedToAmerican(p);
}

/** Profit multiple on a 1-unit stake — the `b` in the Kelly formula. */
function payoutRatio(american) {
  const d = americanToDecimal(american);
  return d === null ? null : d - 1;
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 2 — Devig (removing the bookmaker's margin)
//
// A book prices both sides so implied probabilities sum to > 1. That excess is
// the vig. Devigging redistributes it to recover the book's true estimate.
// Three methods disagree about HOW the vig is distributed; we compute all
// three and take the most conservative result.
// ═══════════════════════════════════════════════════════════════════════════

function clampProb(p) {
  if (!Number.isFinite(p)) return null;
  return Math.max(LIMITS.PROB_MIN, Math.min(LIMITS.PROB_MAX, p));
}

/**
 * ADDITIVE — subtract the overround equally from every outcome.
 * Assumes the book applies vig as a flat amount, independent of price.
 * Accurate mid-range, badly wrong on longshots (can go negative).
 *
 *   fair = p - (totalImplied - 1) / numSides
 */
function devigAdditive(impliedProb, totalImplied, numSides = 2) {
  if (!impliedProb || !totalImplied || totalImplied <= 0) return null;
  if (!numSides || numSides < 2) return null;
  return clampProb(impliedProb - (totalImplied - 1) / numSides);
}

/**
 * MULTIPLICATIVE — scale every outcome down proportionally. The CNM standard.
 * Assumes vig is proportional to price. Slightly favors favorites.
 *
 *   fair = p / totalImplied
 */
function devigMultiplicative(impliedProb, totalImplied) {
  if (!impliedProb || !totalImplied || totalImplied <= 0) return null;
  return clampProb(impliedProb / totalImplied);
}

/**
 * POWER — solve for exponent k such that the devigged probs sum to exactly 1.
 * Handles heavy favorites correctly, which is where additive breaks down.
 *
 *   find k where Σ pᵢ^(1/k) = 1, then fair = p^(1/k)
 *
 * Solved by binary search on k. Monotonic in k, so 64 iterations over
 * [0.01, 20] converges far past double precision.
 */
function devigPower(impliedProb, totalImplied) {
  if (!Number.isFinite(impliedProb) || !Number.isFinite(totalImplied)) return null;
  if (impliedProb <= 0 || impliedProb >= 1 || totalImplied <= 0) return null;
  const p2 = totalImplied - impliedProb;
  // Degenerate two-way shape — fall back to multiplicative.
  if (p2 <= 0 || p2 >= 1) return devigMultiplicative(impliedProb, totalImplied);

  let lo = LIMITS.POWER_K_LO;
  let hi = LIMITS.POWER_K_HI;
  for (let i = 0; i < LIMITS.POWER_ITERATIONS; i++) {
    const mid = (lo + hi) / 2;
    if (Math.pow(impliedProb, 1 / mid) + Math.pow(p2, 1 / mid) > 1) hi = mid;
    else lo = mid;
  }
  const fair = Math.pow(impliedProb, 2 / (lo + hi));
  if (!Number.isFinite(fair) || fair <= 0 || fair >= 1) {
    return devigMultiplicative(impliedProb, totalImplied);
  }
  // Clamped for parity with devigAdditive/devigMultiplicative. Without this, a
  // longshot in a thin market can devig below PROB_MIN and leak a probability
  // the other two methods would never produce. Found by property testing.
  return clampProb(fair);
}

/**
 * POWER, N-way — same solve generalized to any number of outcomes (3-way
 * moneylines, categorical markets like "first goalscorer").
 *
 *   find k where Σ pᵢ^(1/k) = 1 over ALL outcomes
 */
function devigPowerNWay(targetImplied, allImplieds) {
  if (!targetImplied || !Array.isArray(allImplieds)) return null;
  const probs = allImplieds.filter((p) => Number.isFinite(p) && p > 0 && p < 1);
  if (probs.length < 2) return null;

  let lo = LIMITS.POWER_K_LO;
  let hi = LIMITS.POWER_K_HI;
  for (let i = 0; i < LIMITS.POWER_ITERATIONS; i++) {
    const mid = (lo + hi) / 2;
    const sum = probs.reduce((s, p) => s + Math.pow(p, 1 / mid), 0);
    if (sum > 1) hi = mid;
    else lo = mid;
  }
  return Math.pow(targetImplied, 2 / (lo + hi));
}

/**
 * Run all three devig methods at once.
 * @returns {{ additive, multiplicative, power }} fair probabilities
 */
function devigAll(impliedProb, totalImplied, numSides = 2) {
  return {
    additive: devigAdditive(impliedProb, totalImplied, numSides),
    multiplicative: devigMultiplicative(impliedProb, totalImplied),
    power: devigPower(impliedProb, totalImplied),
  };
}

/** Is additive trustworthy at this implied probability? */
function isAdditiveReliable(impliedProb) {
  return Number.isFinite(impliedProb)
    && impliedProb >= LIMITS.ADDITIVE_RELIABLE_MIN
    && impliedProb <= LIMITS.ADDITIVE_RELIABLE_MAX;
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 3 — Expected value
// ═══════════════════════════════════════════════════════════════════════════

/**
 * THE EV formula. Everything else in this file exists to produce a trustworthy
 * `fairProb` to feed into it.
 *
 *   EV% = (fairProb × decimalOdds - 1) × 100
 *
 * Reads as: stake 1 unit, win `decimalOdds` back with probability `fairProb`.
 * Subtract the stake. Positive means the price is better than the true odds.
 */
function evPercent(fairProb, decimalOdds) {
  if (!fairProb || !decimalOdds || fairProb <= 0 || decimalOdds <= 0) return null;
  return (fairProb * decimalOdds - 1) * 100;
}

/** EV% directly from American odds. */
function evPercentFromAmerican(fairProb, american) {
  return evPercent(fairProb, americanToDecimal(american));
}

/**
 * Invert EV back to the fair probability that produced it. Used when the
 * backend only stamped an EV and the frontend needs to re-price it at a
 * different book.
 *
 *   fairProb = (EV%/100 + 1) / decimalOdds
 */
function fairProbFromEv(evPct, decimalOdds) {
  if (!Number.isFinite(evPct) || !Number.isFinite(decimalOdds) || decimalOdds <= 0) return null;
  return (evPct / 100 + 1) / decimalOdds;
}

/**
 * Conservative EV across the three devig methods.
 *
 * We show the MINIMUM, not the average. If the three methods disagree, the
 * disagreement is real uncertainty about the book's margin model, and we would
 * rather understate an edge than invent one.
 *
 * `includeAdditive` is an explicit policy flag because the two call sites
 * genuinely differ:
 *   · Display / sort / product-level SGP → include additive (extra
 *     conservatism ranks "too good to be true" parlays DOWN).
 *   · Per-leg SGP internals → exclude additive (it craters on longshot legs
 *     and those stamps are quality metrics, not a display axis).
 *
 * @returns {{ ev, method } | { ev: null, method: null }}
 */
function worstCaseEv(evs, opts = {}) {
  const includeAdditive = opts.includeAdditive !== false;
  const candidates = [];
  if (includeAdditive && Number.isFinite(evs.additive)) {
    candidates.push({ method: 'additive', ev: evs.additive });
  }
  if (Number.isFinite(evs.multiplicative)) {
    candidates.push({ method: 'multiplicative', ev: evs.multiplicative });
  }
  if (Number.isFinite(evs.power)) {
    candidates.push({ method: 'power', ev: evs.power });
  }
  if (candidates.length === 0) return { ev: null, method: null };
  const min = candidates.reduce((a, b) => (b.ev < a.ev ? b : a));
  return { ev: min.ev, method: min.method };
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 4 — Consensus estimators
//
// One book's price is a noisy sample of the true probability. Aggregating
// across books is how we get a reference sharp enough to price against.
// ═══════════════════════════════════════════════════════════════════════════

/** Median of the finite values. Null when there are none. */
function median(values) {
  if (!Array.isArray(values)) return null;
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return null;
  const sorted = finite.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Outlier-resistant central estimate. Sample sizes here are tiny (2-40 books)
 * and one stale or mispriced book can move a plain mean enough to fabricate an
 * edge, so the estimator changes shape with n:
 *
 *   n = 1     → the value itself
 *   n = 2..3  → median (a trimmed mean would discard too much)
 *   n >= 4    → 20% trimmed mean (drops the tails, keeps the middle)
 *
 * Frontend and backend MUST agree here — this feeds the R-factor, so any
 * divergence shows up directly as pill-vs-sort EV disagreement.
 */
function robustMean(values, opts = {}) {
  const trim = Number.isFinite(opts.trim) ? opts.trim : LIMITS.ROBUST_MEAN_TRIM;
  if (!Array.isArray(values)) return null;
  const finite = values.filter((v) => Number.isFinite(v));
  const n = finite.length;
  if (n === 0) return null;
  if (n === 1) return finite[0];
  const sorted = finite.slice().sort((a, b) => a - b);
  if (n <= 3) {
    return n % 2 === 1
      ? sorted[Math.floor(n / 2)]
      : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
  }
  const cut = Math.min(Math.floor(n / 2) - 1, Math.ceil(trim * n));
  const keep = sorted.slice(cut, n - cut);
  if (keep.length === 0) return sorted[Math.floor(n / 2)];
  return keep.reduce((s, v) => s + v, 0) / keep.length;
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 5 — SGP correlation (the R-factor)
//
// Legs of a same-game parlay are NOT independent. Multiplying leg fair probs
// assumes they are, so the independence product is wrong in both directions
// depending on which way the legs are correlated.
//
// Rather than model correlation ourselves, we read it out of the market: books
// quote the parlay directly, so the ratio between the independence-implied
// price and the actual quoted price IS the market's correlation estimate.
//
//   R = legsProductDecimal / robustMean(bookSgpDecimals)
//
// Worked example — two 2.0 legs imply a 4.00 parlay (25%):
//   · Book quotes 3.00 (33%) → it thinks the combo is MORE likely than
//     independence → POSITIVE correlation → R = 4.00/3.00 = 1.33
//   · Book quotes 8.00 (12.5%) → LESS likely than independence →
//     NEGATIVE correlation → R = 4.00/8.00 = 0.50
//
//   R ≈ 1  → market agrees legs are roughly independent
//   R > 1  → POSITIVE correlation; books price the parlay SHORTER
//   R < 1  → NEGATIVE correlation; books price the parlay LONGER
//
// Then: fairSgpProb = Π(legFairProbs) × R
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Clamp a raw R-factor per the active math policy.
 *
 * The legacy floor of 0.50 is the single worst bug in the platform's math
 * history. A real production case: "Dort Under 1.5 threes + Dort Over 9.5
 * points" are strongly NEGATIVELY correlated (scoring more points usually means
 * making more threes), so books quoted the combo far longer than independence
 * implied — a true R of 0.076. Flooring that to 0.50 multiplied the fair
 * probability by 6.5x and manufactured a +578% edge that did not exist.
 *
 * v2 removes the floor and keeps only the upper cap.
 *
 * @param {number} rawR
 * @param {boolean} mathV2 - true = no floor (correct), false = legacy floor
 */
function clampRFactor(rawR, mathV2) {
  if (rawR == null || !Number.isFinite(rawR)) return 1;
  // v2 is production default (sgpMathV2Enabled ON). Legacy floor only when explicitly OFF.
  if (mathV2 !== false) {
    return rawR > LIMITS.R_FACTOR_MAX ? LIMITS.R_FACTOR_MAX : rawR;
  }
  return Math.max(
    LIMITS.R_FACTOR_MIN_LEGACY,
    Math.min(LIMITS.R_FACTOR_MAX, rawR)
  );
}

/**
 * Cap displayed/sort EV for ghost edges (props vs game lines).
 * Only clips positive EV above the tier cap; negative EV passes through.
 */
function displayEvCap(ev, isPlayerProp) {
  if (!Number.isFinite(ev)) return null;
  const cap = isPlayerProp ? LIMITS.PROP_EV_DISPLAY_CAP : LIMITS.LINE_EV_DISPLAY_CAP;
  return ev > cap ? cap : ev;
}

/**
 * Whether an item should use the player-prop EV cap tier.
 */
function isPlayerPropItem(item) {
  if (!item || typeof item !== 'object') return false;
  return !!(item._isPlayerProp || item.is_player_prop || item.player_name);
}

/**
 * Compute the market-implied correlation factor.
 *
 * @param {number} legsProductDecimal - Π decimal(leg) — the independence price
 * @param {number[]} bookSgpDecimals  - actual quoted parlay decimals per book
 * @param {boolean} mathV2
 * @returns {{ r, rawR, avgBookDecimal, bookCount, clamped }}
 */
function computeRFactor(legsProductDecimal, bookSgpDecimals, mathV2) {
  const decs = (bookSgpDecimals || []).filter((d) => Number.isFinite(d) && d > 1);
  const avg = robustMean(decs);
  if (!Number.isFinite(legsProductDecimal) || legsProductDecimal <= 1 || !avg || avg <= 1) {
    return { r: null, rawR: null, avgBookDecimal: avg ?? null, bookCount: decs.length, clamped: false };
  }
  const rawR = legsProductDecimal / avg;
  const r = clampRFactor(rawR, mathV2);
  return {
    r,
    rawR,
    avgBookDecimal: avg,
    bookCount: decs.length,
    clamped: Math.abs(r - rawR) > 1e-9,
  };
}

/**
 * Correlation-adjusted fair probability for a parlay.
 *
 *   fairSgpProb = Π(legFairProbs) × R
 */
function sgpFairProb(legFairProbs, rFactor) {
  if (!Array.isArray(legFairProbs) || legFairProbs.length === 0) return null;
  let product = 1;
  for (const p of legFairProbs) {
    if (!Number.isFinite(p) || p <= 0) return null;
    product *= p;
  }
  const r = Number.isFinite(rFactor) ? rFactor : 1;
  const raw = product * r;
  return Math.max(LIMITS.SGP_PROB_MIN, Math.min(LIMITS.SGP_PROB_MAX, raw));
}

/**
 * Re-price a stamped SGP EV into a different R-factor / price frame.
 *
 * The backend stamps EV at the best book's price using its own R. To show a
 * per-book EV — or to heal a cache doc written with a floored R — invert to the
 * correlated fair prob, swap the R, and re-apply:
 *
 *   correlatedFP = (ev/100 + 1) / fromDecimal
 *   ev'          = (correlatedFP × (toR / fromR) × toDecimal - 1) × 100
 */
function rescaleSgpEv(ev, fromDecimal, toDecimal, fromR, toR) {
  if (!Number.isFinite(ev) || !Number.isFinite(fromDecimal) || fromDecimal <= 1) return null;
  if (!Number.isFinite(toDecimal) || toDecimal <= 1) return null;
  if (!Number.isFinite(fromR) || fromR <= 0 || !Number.isFinite(toR) || toR <= 0) return null;
  const correlatedFP = fairProbFromEv(ev, fromDecimal);
  if (correlatedFP === null) return null;
  return (correlatedFP * (toR / fromR) * toDecimal - 1) * 100;
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 6 — Stake sizing (Kelly criterion)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kelly optimal stake as a fraction of bankroll.
 *
 *   f* = (b × p - q) / b     where b = profit multiple, q = 1 - p
 *
 * Full Kelly maximizes long-run growth but is brutally volatile, so callers
 * pass a `fraction` (½, ¼ Kelly). Result is floored at 0 (never bet a -EV
 * edge) and capped at KELLY_MAX_FRACTION as a bankroll-ruin guard.
 *
 * @param {number} fairProb - true win probability
 * @param {number} american - the price being offered
 * @param {number} [fraction=1] - Kelly multiplier (0.25 = quarter Kelly)
 */
function kellyFraction(fairProb, american, fraction = 1) {
  if (!Number.isFinite(fairProb) || fairProb <= 0 || fairProb >= 1) return null;
  const b = payoutRatio(american);
  if (b === null || b <= 0) return null;
  const q = 1 - fairProb;
  const full = (b * fairProb - q) / b;
  const scaled = full * (Number.isFinite(fraction) ? fraction : 1);
  return Math.max(0, Math.min(scaled, LIMITS.KELLY_MAX_FRACTION));
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 7 — Full pipeline (the "plug in numbers, get everything" entry point)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Resolve the total implied probability to devig against, and report where it
 * came from. Provenance matters: a same-book pair is ground truth, a cross-book
 * consensus is an estimate, and assumed juice is a guess.
 *
 * @returns {{ totalImplied, source, hasRealOpposite, oppositeAmerican }}
 *   source: 'same_book' | 'cross_book' | 'estimated'
 */
function resolveTotalImplied(impliedProb, oppositeAmerican, totalImpliedOverride, opts = {}) {
  const assumedVig = Number.isFinite(opts.assumedVig)
    ? opts.assumedVig
    : LIMITS.ASSUMED_VIG_DEFAULT;

  // Sentinel sanitize — the feed sends -99900 to mean "no opposing line".
  let opp = oppositeAmerican;
  if (opp != null && (opp < LIMITS.OPPOSITE_ODDS_MIN || opp > LIMITS.OPPOSITE_ODDS_MAX)) {
    opp = null;
  }

  let totalImplied = impliedProb;
  let hasRealOpposite = false;
  let source = 'estimated';

  if (opp !== null && opp !== undefined) {
    const oppImplied = americanToImplied(opp);
    if (oppImplied && oppImplied > 0) {
      const sameBookTotal = impliedProb + oppImplied;
      // A real two-way market always has some vig (total >= 1.0). Below that is
      // a data-quality problem, not a gift.
      if (sameBookTotal >= 1.0) {
        totalImplied = sameBookTotal;
        hasRealOpposite = true;
        source = 'same_book';
      }
    }
  }

  if (!hasRealOpposite
      && Number.isFinite(totalImpliedOverride)
      && totalImpliedOverride > 1) {
    totalImplied = totalImpliedOverride;
    hasRealOpposite = true;
    source = 'cross_book';
  }

  const isExtremeLow = hasRealOpposite && totalImplied < LIMITS.TOTAL_IMPLIED_DFS_FLOOR;
  const isMalformed = hasRealOpposite && totalImplied > LIMITS.TOTAL_IMPLIED_MALFORMED_CEILING;

  if (!hasRealOpposite || isExtremeLow || isMalformed) {
    const estimatedOpposite = assumedVig - impliedProb;
    totalImplied = (estimatedOpposite > 0.01 && estimatedOpposite < 0.99)
      ? impliedProb + estimatedOpposite
      : assumedVig;
    if (!hasRealOpposite) source = 'estimated';
  }

  const oppImplied = clampProb(totalImplied - impliedProb);
  const derivedOppAmerican = oppImplied === null ? null : Math.round(impliedToAmerican(oppImplied));

  return {
    totalImplied,
    source,
    hasRealOpposite,
    oppositeAmerican: (opp !== null && opp !== undefined) ? opp : derivedOppAmerican,
  };
}

/**
 * ONE CALL, EVERY NUMBER. This is the function the admin calculator and every
 * caller should reach for: give it a price and whatever context you have, get
 * back the complete, fully-explained math.
 *
 * IMPORTANT — where positive EV actually comes from:
 * Devigging a single price can never produce +EV. Every method divides that
 * book's own implied probability down by its own overround, so the result is
 * always <= the offered price. A genuine edge only exists when a book's price is
 * compared against a fair probability sourced from SOMEWHERE ELSE — the
 * cross-book consensus. Pass `referenceFairProb` to model that: it prices the
 * offered odds against an external fair estimate instead of self-devigging.
 *
 * @param {Object} input
 * @param {number} input.american - the price being evaluated (required)
 * @param {number} [input.oppositeAmerican] - same-book other side
 * @param {number} [input.totalImpliedOverride] - cross-book consensus total
 * @param {number} [input.referenceFairProb] - external fair prob (consensus).
 *   When supplied, EV is computed against this instead of the self-devig, which
 *   is the only way a positive edge can appear.
 * @param {number} [input.assumedVig] - override the 1.10 fallback
 * @param {boolean} [input.includeAdditive=true] - additive in the min-set
 * @param {number} [input.kellyFraction=0.25]
 * @returns {Object} full breakdown — inputs, vig, three methods, EV, Kelly
 */
function calculate(input = {}) {
  const american = input.american;
  const implied = americanToImplied(american);
  const decimal = americanToDecimal(american);

  if (implied === null || decimal === null) {
    return { valid: false, error: 'Invalid American odds', input };
  }

  const vig = resolveTotalImplied(
    implied,
    input.oppositeAmerican ?? null,
    input.totalImpliedOverride ?? null,
    { assumedVig: input.assumedVig }
  );

  const selfDevig = devigAll(implied, vig.totalImplied, input.numSides ?? 2);

  // When an external reference fair prob is supplied, every method is priced
  // against it — the consensus is the truth and this book's price is the thing
  // being judged. Otherwise we self-devig (always <= 0% EV by construction).
  const ref = Number.isFinite(input.referenceFairProb) ? input.referenceFairProb : null;
  const usingReference = ref !== null && ref > 0 && ref < 1;
  const fair = usingReference
    ? { additive: ref, multiplicative: ref, power: ref }
    : selfDevig;

  const evs = {
    additive: evPercent(fair.additive, decimal),
    multiplicative: evPercent(fair.multiplicative, decimal),
    power: evPercent(fair.power, decimal),
  };

  // Self-devig EVs are always reported separately. Under a reference the three
  // entries in `evs` collapse to the same number (all methods priced against the
  // one consensus figure), so a per-method comparison has to read from here.
  const selfDevigEvs = usingReference
    ? {
      additive: evPercent(selfDevig.additive, decimal),
      multiplicative: evPercent(selfDevig.multiplicative, decimal),
      power: evPercent(selfDevig.power, decimal),
    }
    : evs;

  // Additive is dropped from the min-set when the implied prob is outside the
  // band where it behaves — but we still report its value for transparency.
  const additiveReliable = isAdditiveReliable(implied);
  const best = worstCaseEv(evs, {
    includeAdditive: (input.includeAdditive !== false) && additiveReliable,
  });

  const chosenFair = best.method ? fair[best.method] : null;
  const kf = Number.isFinite(input.kellyFraction) ? input.kellyFraction : 0.25;

  return {
    valid: true,
    input: {
      american,
      oppositeAmerican: input.oppositeAmerican ?? null,
      totalImpliedOverride: input.totalImpliedOverride ?? null,
    },
    odds: {
      american,
      decimal,
      impliedProb: implied,
      impliedPercent: implied * 100,
      payoutRatio: decimal - 1,
    },
    vig: {
      totalImplied: vig.totalImplied,
      vigPercent: (vig.totalImplied - 1) * 100,
      source: vig.source,
      hasRealOpposite: vig.hasRealOpposite,
      oppositeAmerican: vig.oppositeAmerican,
    },
    fairProbs: fair,
    selfDevigProbs: selfDevig,
    evPercents: evs,
    selfDevigEvPercents: selfDevigEvs,
    referenceFairProb: usingReference ? ref : null,
    additiveReliable,
    fairProbSource: usingReference ? 'reference_consensus' : 'self_devig',
    best: {
      evPercent: best.ev,
      method: best.method,
      fairProb: chosenFair,
      fairAmerican: chosenFair === null ? null : Math.round(impliedToAmerican(chosenFair)),
    },
    kelly: {
      fraction: kf,
      full: chosenFair === null ? null : kellyFraction(chosenFair, american, 1),
      scaled: chosenFair === null ? null : kellyFraction(chosenFair, american, kf),
    },
  };
}

/**
 * Full SGP calculation — legs in, correlation-adjusted EV out.
 *
 * @param {Object} input
 * @param {Array} input.legs - [{ american, oppositeAmerican?, totalImpliedOverride? }]
 * @param {number[]} input.bookSgpDecimals - quoted parlay decimals across books
 * @param {number} input.offeredAmerican - the parlay price being evaluated
 * @param {boolean} [input.mathV2=true] - false reproduces the legacy R floor
 */
function calculateSgp(input = {}) {
  const legs = Array.isArray(input.legs) ? input.legs : [];
  if (legs.length < 2) return { valid: false, error: 'SGP requires at least 2 legs' };

  const mathV2 = input.mathV2 !== false;
  const legResults = [];
  let legsProductDecimal = 1;
  const fairByMethod = { additive: 1, multiplicative: 1, power: 1 };

  for (const leg of legs) {
    const r = calculate({ ...leg, includeAdditive: input.includeAdditive });
    if (!r.valid) return { valid: false, error: `Invalid leg: ${leg.american}` };
    legResults.push(r);
    legsProductDecimal *= r.odds.decimal;
    fairByMethod.additive *= (r.fairProbs.additive ?? 0);
    fairByMethod.multiplicative *= (r.fairProbs.multiplicative ?? 0);
    fairByMethod.power *= (r.fairProbs.power ?? 0);
  }

  const rInfo = computeRFactor(legsProductDecimal, input.bookSgpDecimals, mathV2);
  const r = Number.isFinite(rInfo.r) ? rInfo.r : 1;

  const offered = input.offeredAmerican ?? decimalToAmerican(legsProductDecimal);
  const offeredDecimal = americanToDecimal(offered);

  const correlatedFair = {
    additive: sgpFairProb([fairByMethod.additive], r),
    multiplicative: sgpFairProb([fairByMethod.multiplicative], r),
    power: sgpFairProb([fairByMethod.power], r),
  };

  const evs = {
    additive: evPercent(correlatedFair.additive, offeredDecimal),
    multiplicative: evPercent(correlatedFair.multiplicative, offeredDecimal),
    power: evPercent(correlatedFair.power, offeredDecimal),
  };

  const best = worstCaseEv(evs, { includeAdditive: input.includeAdditive !== false });

  return {
    valid: true,
    mathV2,
    legs: legResults,
    independence: {
      legsProductDecimal,
      legsProductAmerican: decimalToAmerican(legsProductDecimal),
      fairProbProduct: fairByMethod,
    },
    correlation: rInfo,
    correlatedFairProbs: correlatedFair,
    offered: { american: offered, decimal: offeredDecimal },
    evPercents: evs,
    best: { evPercent: best.ev, method: best.method },
  };
}

/**
 * Per-book SGP EV from optimizer method stamps, with leave-one-out R correction.
 *
 * This is the single formula for the card pill, sort axis, and backend
 * `clean_ev_percent` stamping. Backend (evCleanPerBook) and frontend
 * (canonicalEv) must both call this — never reimplement inline.
 *
 * The method stamps (ev_method1/2/3) are computed at the SGP's best-book price
 * using R_backend built from ALL consensus books. When evaluating a specific
 * book's price, we recompute R_excl without that book and rescale:
 *
 *   ev_excl = (correlatedFP × (R_excl / R_backend) × selDec − 1) × 100
 *
 * Return value is min(additive, multiplicative, power) — matching the math
 * block's "✓ Used" row and the client's conservative preference.
 *
 * @param {Object} input
 * @param {number} input.combinedPrice - combined american at best book (stamp frame)
 * @param {number} input.bookPrice - this book's american price to evaluate
 * @param {number} [input.evMethod1Additive]
 * @param {number} [input.evMethod2Multiplicative]
 * @param {number} [input.evMethod3Power]
 * @param {number} [input.rFactor=1]
 * @param {number} [input.legsProductDecimal]
 * @param {number[]} [input.otherBookDecimals] - other books' SGP decimals (LOO set)
 * @param {boolean} [input.mathV2=false]
 * @returns {number|null}
 */
function sgpMethodRowsFromStamps(input = {}) {
  const combinedPrice = input.combinedPrice;
  const bookPrice = input.bookPrice;
  if (!Number.isFinite(combinedPrice) || !Number.isFinite(bookPrice)) return null;

  const bestDec = americanToDecimal(combinedPrice);
  const selDec = americanToDecimal(bookPrice);
  if (bestDec == null || selDec == null || bestDec <= 1 || selDec <= 1) return null;

  const rBackend = Number.isFinite(input.rFactor) && input.rFactor > 0 ? input.rFactor : 1;
  const combinedDec = Number.isFinite(input.legsProductDecimal) ? input.legsProductDecimal : null;

  let rExclRatio = 1;
  const others = Array.isArray(input.otherBookDecimals)
    ? input.otherBookDecimals.filter((d) => Number.isFinite(d) && d > 1)
    : [];

  if (combinedDec != null && combinedDec > 1 && rBackend > 0 && others.length >= 1) {
    const avgExcl = robustMean(others);
    if (avgExcl != null && avgExcl > 1) {
      const rExcl = clampRFactor(combinedDec / avgExcl, input.mathV2 === true);
      rExclRatio = rExcl / rBackend;
    }
  }

  const mkRow = (stampedEv) => {
    if (!Number.isFinite(stampedEv)) return null;
    const fairProb = (stampedEv / 100 + 1) / bestDec;
    const ev = (fairProb * rExclRatio * selDec - 1) * 100;
    return Number.isFinite(ev) ? { fairProb, ev: parseFloat(ev.toFixed(2)) } : null;
  };

  const additive = mkRow(input.evMethod1Additive);
  const multiplicative = mkRow(input.evMethod2Multiplicative);
  const power = mkRow(input.evMethod3Power);
  if (!additive && !multiplicative && !power) return null;

  return {
    additive,
    multiplicative,
    power,
    looApplied: others.length >= 1 && combinedDec != null && combinedDec > 1 && rExclRatio !== 1,
    rExclRatio,
  };
}

/**
 * Canonical cached-SGP per-book EV: min(add, mult, pow) with LOO R-ratio when peer books exist.
 * @returns {number|null}
 */
function sgpPerBookEvFromStamps(input = {}) {
  const rows = sgpMethodRowsFromStamps(input);
  if (!rows) return null;
  const evs = [rows.additive?.ev, rows.multiplicative?.ev, rows.power?.ev].filter((v) => Number.isFinite(v));
  if (!evs.length) return null;
  return Math.min(...evs);
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 8 — Self-verification
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Prove the conversions are mutually consistent and the devig methods sum to 1.
 * Runs in-process so the admin page can display a live green/red integrity
 * check rather than asking anyone to trust the code.
 *
 * @returns {{ passed: boolean, checks: Array<{name, passed, detail}> }}
 */
function selfCheck() {
  const checks = [];
  const near = (a, b, eps = 1e-9) => Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < eps;

  const prices = [-1000, -250, -110, -101, 101, 110, 250, 1000];

  checks.push({
    name: 'american → decimal → american round-trips',
    passed: prices.every((p) => near(decimalToAmerican(americanToDecimal(p)), p, 1e-6)),
    detail: 'decimalToAmerican(americanToDecimal(p)) === p',
  });

  checks.push({
    name: 'american → implied → american round-trips',
    passed: prices.every((p) => near(impliedToAmerican(americanToImplied(p)), p, 1e-6)),
    detail: 'impliedToAmerican(americanToImplied(p)) === p',
  });

  checks.push({
    name: 'implied and decimal agree (p = 1/d)',
    passed: prices.every((p) => near(americanToImplied(p), decimalToImplied(americanToDecimal(p)), 1e-12)),
    detail: 'americanToImplied(p) === 1 / americanToDecimal(p)',
  });

  checks.push({
    name: 'payoutRatio equals decimal - 1',
    passed: prices.every((p) => near(payoutRatio(p), americanToDecimal(p) - 1)),
    detail: 'Kelly b === decimal - 1 for both odds signs',
  });

  checks.push({
    name: 'even money renders as +100 (CNM convention)',
    passed: near(impliedToAmerican(0.5), 100) && near(americanToDecimal(100), 2.0),
    detail: 'impliedToAmerican(0.5) === +100, not -100',
  });

  // A fair two-way market devigs back to probabilities summing to 1.
  const pA = americanToImplied(-110);
  const pB = americanToImplied(-110);
  const ti = pA + pB;
  const multSum = devigMultiplicative(pA, ti) + devigMultiplicative(pB, ti);
  const addSum = devigAdditive(pA, ti) + devigAdditive(pB, ti);
  const powSum = devigPower(pA, ti) + devigPower(pB, ti);

  checks.push({
    name: 'multiplicative devig sums to 1.0',
    passed: near(multSum, 1, 1e-9),
    detail: `sum = ${multSum}`,
  });
  checks.push({
    name: 'additive devig sums to 1.0',
    passed: near(addSum, 1, 1e-9),
    detail: `sum = ${addSum}`,
  });
  checks.push({
    name: 'power devig sums to 1.0',
    passed: near(powSum, 1, 1e-6),
    detail: `sum = ${powSum}`,
  });

  // A perfectly fair coin-flip price at true 50% must be exactly 0% EV.
  checks.push({
    name: 'fair price yields exactly 0% EV',
    passed: near(evPercent(0.5, 2.0), 0, 1e-12),
    detail: 'evPercent(0.5, 2.0) === 0',
  });

  // EV inversion must be lossless.
  checks.push({
    name: 'EV inverts back to its fair probability',
    passed: near(fairProbFromEv(evPercent(0.55, 2.0), 2.0), 0.55, 1e-12),
    detail: 'fairProbFromEv(evPercent(p, d), d) === p',
  });

  // The bug this whole audit exists to catch.
  checks.push({
    name: 'v2 R-factor does NOT floor a genuine low R',
    passed: near(clampRFactor(0.076, true), 0.076),
    detail: 'clampRFactor(0.076, mathV2=true) === 0.076',
  });
  checks.push({
    name: 'legacy R-factor DOES floor at 0.50 (documents the bug)',
    passed: near(clampRFactor(0.076, false), 0.50),
    detail: 'clampRFactor(0.076, mathV2=false) === 0.50',
  });

  // robustMean shape transitions.
  checks.push({
    name: 'robustMean uses median at n<=3',
    passed: near(robustMean([1, 2, 100]), 2),
    detail: 'robustMean([1,2,100]) === 2 (median, not mean)',
  });
  checks.push({
    name: 'robustMean trims tails at n>=4',
    passed: near(robustMean([1, 2, 3, 1000]), 2.5),
    detail: 'robustMean([1,2,3,1000]) === 2.5 (outlier trimmed)',
  });

  return { passed: checks.every((c) => c.passed), checks };
}

export {
  LIMITS,
  // conversions
  americanToImplied,
  americanToDecimal,
  decimalToImplied,
  impliedToDecimal,
  impliedToAmerican,
  decimalToAmerican,
  payoutRatio,
  // devig
  clampProb,
  devigAdditive,
  devigMultiplicative,
  devigPower,
  devigPowerNWay,
  devigAll,
  isAdditiveReliable,
  // ev
  evPercent,
  evPercentFromAmerican,
  fairProbFromEv,
  worstCaseEv,
  // consensus
  median,
  robustMean,
  // sgp
  clampRFactor,
  computeRFactor,
  displayEvCap,
  isPlayerPropItem,
  sgpFairProb,
  rescaleSgpEv,
  // staking
  kellyFraction,
  // pipeline
  resolveTotalImplied,
  calculate,
  calculateSgp,
  sgpPerBookEvFromStamps,
  sgpMethodRowsFromStamps,
  // verification
  selfCheck,
};
